'use client';

import type { ApplicationRoleDto } from '@terra/shared-contracts-applications';
import { Button } from '../../components/styled/button';
import { LegacyDialog } from '../../components/dialog';
import { Stack } from '@terra/ui-styled-system';
import { Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useState, type ReactNode } from 'react';
import { RoleForm } from './RoleForm';

export interface RoleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  applicationId: string;
  role?: ApplicationRoleDto;
  hasGitIntegration?: boolean;
  onSave: (role: {
    name: string;
    loginUrl?: string;
    description?: string;
    enableAiScanning?: boolean;
  }) => Promise<void>;
  onRoleAnalyze?: (roleId: string) => Promise<{
    jobId: string;
  }>;
  formKey?: string;
  headerContent?: ReactNode;
}
export function RoleFormModal({
  isOpen,
  onClose,
  applicationId,
  role,
  hasGitIntegration = false,
  onSave,
  onRoleAnalyze,
  formKey,
  headerContent,
}: RoleFormModalProps) {
  const t = useTranslations('designSystem.applicationRoles');
  const [activeRole, setActiveRole] = useState(role);

  if (isOpen && activeRole !== role) {
    setActiveRole(role);
  }

  const handleSave = async (roleData: {
    name: string;
    loginUrl?: string;
    description?: string;
    enableAiScanning?: boolean;
  }) => {
    await onSave(roleData);
    onClose();
  };

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={({ open }) => !open && onClose()}
      lazyMount
      unmountOnExit
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content maxWidth="600px" width="90vw" maxHeight="90vh">
          <Stack gap="6" p="6" maxHeight="90vh" overflowY="auto">
            <Stack gap="1">
              <LegacyDialog.Title>
                {activeRole ? t('editRole') : t('createRole')}
              </LegacyDialog.Title>
            </Stack>
            {headerContent}
            <RoleForm
              key={formKey}
              applicationId={applicationId}
              role={activeRole}
              hasGitIntegration={hasGitIntegration}
              onSave={handleSave}
              onCancel={onClose}
              onRoleAnalyze={onRoleAnalyze}
              isModal={true}
            />
          </Stack>
          <LegacyDialog.CloseTrigger
            asChild
            position="absolute"
            top="2"
            right="2"
          >
            <Button variant="ghost" size="xs" aria-label="Close Dialog">
              <Cancel01Icon size={14} />
            </Button>
          </LegacyDialog.CloseTrigger>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
